import { useEffect, useMemo, useRef, useState } from 'react';
import {
  NO_LOCK, PRESENCE, PRESENCE_LOCK_RECHECK_MS, PRESENCE_VIEW, isLock, isRecheckableLock,
  isServerConfirmedEntry, nextPresenceDeadline, ownPresenceEntry, presenceView, readPresence, renewLock,
} from './presenceState';

// One sessionStorage record per driver session and rider: whether the rider has had the remote,
// and the open lock episode, so a reload neither forgets the first nor buys a fresh grace. Other
// session starts' records expire. Storage may be disabled or full; the in-memory state still applies.
const recordScope = (driverUid, passengerId) => `strive:presence:${driverUid}:${passengerId}:`;
const recordKey = (driverUid, passengerId, sessionStart) =>
  driverUid && passengerId && Number.isFinite(sessionStart) && sessionStart > 0
    ? `${recordScope(driverUid, passengerId)}${sessionStart}` : '';

function readRecord(key) {
  try {
    const value = key ? JSON.parse(sessionStorage.getItem(key)) : null;
    return { shown: value?.shown === true, lock: isLock(value?.lock) ? value.lock : null };
  } catch { return { shown: false, lock: null }; }
}

function updateRecord(key, patch) {
  if (!key) return;
  try {
    const next = { ...readRecord(key), ...patch };
    if (next.shown || next.lock) sessionStorage.setItem(key, JSON.stringify(next));
    else sessionStorage.removeItem(key);
  } catch { /* storage unavailable */ }
}

function expireOtherSessions(driverUid, passengerId, keep) {
  if (!keep) return;
  try {
    const scope = recordScope(driverUid, passengerId);
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(scope) && key !== keep) sessionStorage.removeItem(key);
    }
  } catch { /* storage unavailable */ }
}

// What the rider sees, derived from their live_pax_coords entry (presenceState.js). A
// recoverable lock is re-checked for PRESENCE_LOCK_GRACE_MS, under a notice on the remote once
// the rider has had it this driver session, and requests a fresh position at once and again
// after iOS's verdict hold-off, so the driver app re-evaluates before RideEnded.
export default function usePassengerPresence({
  active, loaded, passengerId, livePax, override, writeSettledAt, requestFreshPosition,
  driverUid, sessionStart,
}) {
  const key = recordKey(driverUid, passengerId, sessionStart);
  const keyRef = useRef(key);
  keyRef.current = key;
  const [lock, setLock] = useState(NO_LOCK);
  const [entrySeenBefore, setEntrySeenBefore] = useState(false);
  // The record key ('' while unknown) under which this page has shown the rider the remote.
  const [shownUnder, setShownUnder] = useState(null);
  const shownBefore = useMemo(() => readRecord(key).shown, [key]);
  const wasPresent = shownUnder === key || shownBefore;
  const [, setTick] = useState(0);
  const request = useRef(requestFreshPosition);
  useEffect(() => { request.current = requestFreshPosition; });

  const sessionActive = Boolean(active);
  // Joining ends for good once a snapshot shows the rider's own server-confirmed entry.
  const entrySeenNow = Boolean(loaded && passengerId) &&
    isServerConfirmedEntry(ownPresenceEntry(livePax, passengerId));
  const entrySeen = entrySeenBefore || entrySeenNow;
  useEffect(() => {
    if (entrySeenNow) setEntrySeenBefore(true);
  }, [entrySeenNow]);
  const now = Date.now();
  const presence = readPresence({ loaded, passengerId, livePax, entrySeen, writeSettledAt, now });
  const view = presenceView({ presence, override, active: sessionActive, lockedAt: lock.at, wasPresent, now });
  const recheck = isRecheckableLock(presence, { active: sessionActive, override });

  useEffect(() => {
    if (!recheck) {
      setLock(NO_LOCK);
      return undefined;
    }
    // A reload into the lock this session already recorded is a page return, not a new episode.
    const stored = readRecord(keyRef.current).lock;
    setLock(stored ? renewLock(stored, Date.now()) : { at: Date.now(), renewed: false });
    if (request.current) request.current();
    const followUp = setTimeout(() => {
      if (request.current) request.current();
    }, PRESENCE_LOCK_RECHECK_MS);
    return () => clearTimeout(followUp);
  }, [recheck]);

  // The heartbeat stops while hidden, so a lock that landed then was judged on a stale
  // coordinate. Returning pushes a fresh one, and restarts the grace once per lock episode.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'hidden') setLock(current => renewLock(current, Date.now()));
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Keep the episode for a reload; drop it once a loaded page sees the lock over.
  useEffect(() => {
    if (!loaded) return;
    if (lock.at !== null) updateRecord(key, { lock });
    else if (!recheck) updateRecord(key, { lock: null });
  }, [loaded, key, lock, recheck]);

  useEffect(() => {
    expireOtherSessions(driverUid, passengerId, key);
  }, [driverUid, passengerId, key]);

  const remoteShown = sessionActive && view === PRESENCE_VIEW.REMOTE;
  useEffect(() => {
    if (!remoteShown) return;
    setShownUnder(key);
    updateRecord(key, { shown: true });
  }, [remoteShown, key]);

  // Grace expiries change the view without a snapshot; re-render when the next one lands.
  const deadline = nextPresenceDeadline({ writeSettledAt: entrySeen ? null : writeSettledAt, lockedAt: lock.at, now });
  useEffect(() => {
    if (deadline === null) return undefined;
    const timer = setTimeout(() => setTick(tick => tick + 1), Math.max(0, deadline - Date.now()));
    return () => clearTimeout(timer);
  }, [deadline]);

  return {
    view,
    // RECHECKING keeps the remote exactly as present: RemoteTabs drops the Queue tab and Table
    // logs proximity_locked_ride_ended_onhistory for a rider who reads as out of range.
    paxProximity: presence.state === PRESENCE.PRESENT || view === PRESENCE_VIEW.RECHECKING,
  };
}
